import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import RightAdRail from "@/components/RightAdRail";
import { api } from "@/lib/api";

const CAT_COLOR = {
  Speaking: "bg-[#C7DCF7] text-[#1F2547]",
  Writing: "bg-[#D4EDDB] text-[#1F2547]",
  Grammar: "bg-[#9F87D8] text-white",
  Reading: "bg-[#EFE9F7] text-[#1F2547]",
  Listening: "bg-[#FFE4F1] text-[#1F2547]",
};

function BlogCard({ b }) {
  const isExternal = b.url && /^https?:\/\//i.test(b.url);
  const inner = (
    <>
      <div className="aspect-[4/3] overflow-hidden bg-[#EFE9F7]">
        {b.image ? (
          <img
            src={b.image}
            alt={b.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#9F87D8] text-4xl font-black">
            ✦
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          {b.category && (
            <span
              className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${CAT_COLOR[b.category] || "bg-[#EFE9F7] text-[#1F2547]"}`}
            >
              {b.category}
            </span>
          )}
          {b.read_time && (
            <span className="text-xs font-medium text-[#5B6079]">
              {b.read_time}
            </span>
          )}
        </div>
        <h3 className="font-display text-xl font-black tracking-tight text-[#1F2547] leading-snug">
          {b.title}
        </h3>
        {b.excerpt && (
          <p className="mt-2 text-[#5B6079] font-medium text-sm">{b.excerpt}</p>
        )}
        <div className="mt-5 flex items-center justify-between pt-4 border-t border-[#1F2547]/8">
          <span className="text-xs text-[#5B6079] font-medium">
            {b.date || ""}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-bold text-[#1F2547] group-hover:text-[#9F87D8]">
            Read <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </>
  );

  const className =
    "flex flex-col bg-white rounded-3xl brutal-shadow overflow-hidden hover-pop group";

  if (!b.url) {
    return (
      <div data-testid={`blog-card-${b.id}`} className={className}>
        {inner}
      </div>
    );
  }
  if (isExternal) {
    return (
      <a
        href={b.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`blog-card-${b.id}`}
        className={className}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={b.url}
      data-testid={`blog-card-${b.id}`}
      className={className}
    >
      {inner}
    </Link>
  );
}

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    api
      .get("/blogs")
      .then((r) => setBlogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(blogs.map((b) => b.category).filter(Boolean)))];
  const filtered =
    activeCat === "All"
      ? blogs
      : blogs.filter((b) => b.category === activeCat);

  return (
    <main data-testid="blogs-page" className="min-h-[60vh]">
      <RightAdRail />
      {/* Header */}
      <section className="bg-[#D4EDDB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F2547]/70">
            / Blog
          </span>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter mt-4 max-w-4xl text-[#1F2547]">
            Words, well-chosen.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-[#1F2547]/75 font-medium max-w-2xl">
            Lessons, hot takes, and quiet wisdom on the English language — from
            the Percept Academy team.
          </p>

          {blogs.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-full brutal-shadow-sm">
                <Search className="w-4 h-4 text-[#5B6079]" />
                <input
                  data-testid="blog-search-input"
                  placeholder="Search articles…"
                  className="bg-transparent outline-none text-sm font-medium w-44 placeholder:text-[#5B6079] text-[#1F2547]"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`blog-filter-${c.toLowerCase()}`}
                    onClick={() => setActiveCat(c)}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                      activeCat === c
                        ? "bg-[#1F2547] text-white"
                        : "bg-white text-[#1F2547] hover:bg-[#1F2547] hover:text-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* List */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {loading ? (
          <div className="text-[#5B6079] font-medium text-center py-12">
            Loading blogs…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-[#5B6079] font-medium text-center py-12 bg-white rounded-3xl brutal-shadow-sm">
            {blogs.length === 0
              ? "No blogs yet. New articles are on the way — check back soon."
              : `No blogs in "${activeCat}" yet.`}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b) => (
              <BlogCard key={b.id} b={b} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
